import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

import {
    FaWhatsapp,
    FaFacebookF,
    FaXTwitter,
    FaLinkedinIn,
    FaLink,
    FaArrowRight,
    FaClock,
    FaChevronRight,
    FaHotel,
    FaCompass,
    FaShareNodes,
} from "react-icons/fa6";

import {
    FaHome,
    FaCalendarAlt,
} from "react-icons/fa";


export default function BlogDetail() {
    const { slug } = useParams();

    const [blog, setBlog] = useState(null);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchBlog = async () => {
            setLoading(true);

            try {
                const res = await axios.get(
                    `https://dubaihotelrates.com/api/blogs/${slug}`
                );

                setBlog(res.data);
            } catch (err) {
                console.error("Failed to load blog:", err);
                setBlog(null);
            } finally {
                setLoading(false);
            }
        };

        const fetchLatest = async () => {
            try {
                const res = await axios.get(
                    "https://dubaihotelrates.com/api/blogs"
                );

                setLatestBlogs(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error("Failed to load latest blogs:", err);
                setLatestBlogs([]);
            }
        };

        fetchBlog();
        fetchLatest();
    }, [slug]);

    const currentUrl =
        typeof window !== "undefined"
            ? window.location.href
            : `https://dubaihotelrates.com/blogs/${slug}`;

    const bannerImage = blog?.banner_image
        ? `https://uaediscountedhotels.com/uploads/blogs/banners/${blog.banner_image}`
        : blog?.image
            ? `https://uaediscountedhotels.com/uploads/blogs/${blog.image}`
            : "https://uaediscountedhotels.com/images/default-og.png";

    const formatDate = (date) => {
        if (!date) return "";

        return new Date(date).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    };

    const formatShortDate = (date) => {
        if (!date) return "";

        return new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const readingTime = useMemo(() => {
        if (!blog?.content) return "5 min read";

        const text = blog.content.replace(/<[^>]*>/g, " ");
        const words = text.trim().split(/\s+/).length;
        const minutes = Math.max(1, Math.ceil(words / 200));

        return `${minutes} min read`;
    }, [blog]);

    const relatedBlogs = latestBlogs
        .filter((item) => item.slug !== blog?.slug)
        .filter((item) => {
            if (!blog?.category?.id) return true;

            return item.category?.id === blog.category.id;
        })
        .slice(0, 3);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(currentUrl);

            setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (error) {
            console.error("Copy failed:", error);
        }
    };

    const shareButtons = [
        {
            name: "WhatsApp",
            icon: <FaWhatsapp />,
            href: `https://wa.me/?text=${encodeURIComponent(
                `${blog?.title} ${currentUrl}`
            )}`,
            className:
                "bg-[#25D366] hover:bg-[#1ebe5d]",
        },
        {
            name: "Facebook",
            icon: <FaFacebookF />,
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                currentUrl
            )}`,
            className:
                "bg-[#1877F2] hover:bg-[#0d65d9]",
        },
        {
            name: "X",
            icon: <FaXTwitter />,
            href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                currentUrl
            )}&text=${encodeURIComponent(blog?.title || "")}`,
            className:
                "bg-[#111827] hover:bg-black",
        },
        {
            name: "LinkedIn",
            icon: <FaLinkedinIn />,
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                currentUrl
            )}`,
            className:
                "bg-[#0A66C2] hover:bg-[#084f96]",
        },
    ];

    if (loading) {
        return (
            <div className="min-h-[70vh] bg-[#F6F9FD] flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="relative mx-auto h-16 w-16">
                        <div className="absolute inset-0 rounded-full border-4 border-[#DCE8F7]" />

                        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#174EA6] border-r-[#FFC928] animate-spin" />
                    </div>

                    <p className="mt-5 text-sm font-medium text-[#64748B]">
                        Loading travel story...
                    </p>
                </div>
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="min-h-[70vh] bg-[#F6F9FD] flex items-center justify-center px-4">
                <div className="max-w-md text-center">
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-[#EAF3FF] text-[#174EA6]">
                        <FaCompass className="text-3xl" />
                    </div>

                    <h1 className="mt-6 text-3xl font-bold text-[#0F172A]">
                        Story Not Found
                    </h1>

                    <p className="mt-3 text-sm leading-7 text-[#64748B]">
                        We couldn't find the travel story you're looking for.
                        Explore our latest travel guides and inspiration instead.
                    </p>

                    <Link
                        to="/blogs"
                        className="
                            mt-7
                            inline-flex
                            items-center
                            gap-2
                            rounded-xl
                            bg-gradient-to-r
                            from-[#174EA6]
                            to-[#FFC928]
                            px-6
                            py-3
                            text-sm
                            font-semibold
                            text-white
                            shadow-lg
                            shadow-blue-900/10
                            transition
                            hover:-translate-y-0.5
                        "
                    >
                        Explore Blogs
                        <FaArrowRight className="text-xs" />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{blog.meta_title || blog.title}</title>

                <meta
                    name="description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        blog.title
                    }
                />

                <link
                    rel="canonical"
                    href={`https://dubaihotelrates.com/blogs/${blog.slug}`}
                />

                <meta
                    property="og:title"
                    content={blog.meta_title || blog.title}
                />

                <meta
                    property="og:description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        blog.title
                    }
                />

                <meta property="og:type" content="article" />

                <meta
                    property="og:url"
                    content={`https://dubaihotelrates.com/blogs/${blog.slug}`}
                />

                <meta
                    property="og:image"
                    content={bannerImage}
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content={blog.meta_title || blog.title}
                />

                <meta
                    name="twitter:description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        blog.title
                    }
                />

                <meta
                    name="twitter:image"
                    content={bannerImage}
                />

                <meta
                    name="robots"
                    content="index, follow"
                />
            </Helmet>

            <main className="bg-[#F6F9FD] text-[#0F172A]">

                {/* =====================================================
                    HERO
                ====================================================== */}

                <section className="relative min-h-[540px] overflow-hidden md:min-h-[600px]">

                    <img
                        src={bannerImage}
                        alt={blog.title}
                        className="
                            absolute
                            inset-0
                            h-full
                            w-full
                            object-cover
                        "
                    />

                    {/* Dark cinematic overlay */}
                    <div
                        className="
                            absolute
                            inset-0
                            bg-gradient-to-b
                            from-[#001A2B]/80
                            via-[#002B45]/65
                            to-[#001522]/95
                        "
                    />

                    {/* Blue glow */}
                    <div
                        className="
                            absolute
                            -left-32
                            top-20
                            h-72
                            w-72
                            rounded-full
                            bg-[#174EA6]/30
                            blur-3xl
                        "
                    />

                    {/* Gold glow */}
                    <div
                        className="
                            absolute
                            -right-32
                            bottom-10
                            h-80
                            w-80
                            rounded-full
                            bg-[#FFC928]/15
                            blur-3xl
                        "
                    />

                    <div className="relative z-10 mx-auto flex min-h-[540px] max-w-7xl flex-col justify-center px-5 pb-20 pt-28 md:min-h-[600px] md:px-8">

                        {/* Breadcrumb */}
                        <div className="mb-8 flex flex-wrap items-center gap-2 text-xs text-white/65 md:text-sm">

                            <Link
                                to="/"
                                className="flex items-center gap-1.5 transition hover:text-white"
                            >
                                <FaHome className="text-[10px]" />
                                Home
                            </Link>

                            <FaChevronRight className="text-[8px] text-white/40" />

                            <Link
                                to="/blogs"
                                className="transition hover:text-white"
                            >
                                Travel Journal
                            </Link>

                            <FaChevronRight className="text-[8px] text-white/40" />

                            <span className="max-w-[220px] truncate text-white/85">
                                {blog.title}
                            </span>

                        </div>

                        {/* Category */}
                        <div className="mb-5">
                            <span
                                className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    rounded-full
                                    border
                                    border-white/20
                                    bg-white/10
                                    px-4
                                    py-2
                                    text-[11px]
                                    font-bold
                                    uppercase
                                    tracking-[0.18em]
                                    text-white
                                    backdrop-blur-md
                                "
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-[#FFC928]" />

                                {blog.category?.name || "Travel Guide"}
                            </span>
                        </div>

                        {/* Title */}
                        <h1
                            className="
                                max-w-5xl
                                text-3xl
                                font-bold
                                leading-[1.08]
                                tracking-tight
                                text-white
                                sm:text-4xl
                                md:text-5xl
                                lg:text-6xl
                                xl:text-7xl
                            "
                        >
                            {blog.title}
                        </h1>

                        {/* Excerpt */}
                        {blog.excerpt && (
                            <p
                                className="
                                    mt-6
                                    max-w-3xl
                                    text-sm
                                    leading-7
                                    text-white/75
                                    sm:text-base
                                    md:text-lg
                                    md:leading-8
                                "
                            >
                                {blog.excerpt}
                            </p>
                        )}

                        {/* Meta */}
                        <div
                            className="
                                mt-8
                                flex
                                flex-wrap
                                items-center
                                gap-x-6
                                gap-y-3
                                text-xs
                                text-white/70
                                sm:text-sm
                            "
                        >
                            <div className="flex items-center gap-2">
                                <FaCalendarAlt className="text-[#FFC928]" />
                                {formatDate(blog.created_at)}
                            </div>

                            <div className="h-4 w-px bg-white/20" />

                            <div className="flex items-center gap-2">
                                <FaClock className="text-[#FFC928]" />
                                {readingTime}
                            </div>
                        </div>

                    </div>

                    {/* Bottom accent */}
                    <div
                        className="
                            absolute
                            bottom-0
                            left-0
                            h-1
                            w-full
                            bg-gradient-to-r
                            from-[#174EA6]
                            via-[#174EA6]
                            to-[#FFC928]
                        "
                    />

                </section>


                {/* =====================================================
                    ARTICLE AREA
                ====================================================== */}

                <section className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-16 lg:px-8">

                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_330px] xl:gap-16">

                        {/* =================================================
                            ARTICLE
                        ================================================== */}

                        <article className="min-w-0">

                            {/* Intro information card */}
                            <div
                                className="
                                    relative
                                    -mt-24
                                    mb-8
                                    rounded-2xl
                                    border
                                    border-[#DCE8F7]
                                    bg-white
                                    p-5
                                    shadow-[0_20px_60px_rgba(15,23,42,0.08)]
                                    sm:p-7
                                    md:p-9
                                "
                            >
                                <div className="absolute left-0 top-0 h-1 w-full rounded-t-2xl bg-gradient-to-r from-[#174EA6] to-[#FFC928]" />

                                <div className="flex flex-wrap items-center gap-3">

                                    <span
                                        className="
                                            rounded-full
                                            bg-[#EAF3FF]
                                            px-3
                                            py-1.5
                                            text-[10px]
                                            font-bold
                                            uppercase
                                            tracking-wider
                                            text-[#174EA6]
                                        "
                                    >
                                        {blog.category?.name ||
                                            "Travel"}
                                    </span>

                                    <span className="text-xs text-[#94A3B8]">
                                        Published {formatShortDate(blog.created_at)}
                                    </span>

                                </div>

                                <div className="mt-6 flex items-start gap-4">

                                    <div className="hidden h-10 w-1 shrink-0 rounded-full bg-gradient-to-b from-[#174EA6] to-[#FFC928] sm:block" />

                                    <p
                                        className="
                                            text-base
                                            font-medium
                                            leading-8
                                            text-[#475569]
                                            sm:text-lg
                                            md:text-xl
                                            md:leading-9
                                        "
                                    >
                                        {blog.excerpt ||
                                            "Discover inspiring travel stories, practical tips and ideas to make your next journey unforgettable."}
                                    </p>

                                </div>
                            </div>


                            {/* Content + desktop share rail */}
                            <div className="grid grid-cols-1 gap-8 xl:grid-cols-[54px_minmax(0,1fr)]">

                                {/* Share rail */}
                                <div className="hidden xl:block">

                                    <div className="sticky top-28 flex flex-col items-center gap-3">

                                        <span className="mb-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#94A3B8] [writing-mode:vertical-rl]">
                                            Share
                                        </span>

                                        {shareButtons.map((item) => (
                                            <a
                                                key={item.name}
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Share on ${item.name}`}
                                                className={`
                                                    flex
                                                    h-10
                                                    w-10
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    text-sm
                                                    text-white
                                                    shadow-sm
                                                    transition
                                                    hover:-translate-y-1
                                                    hover:shadow-lg
                                                    ${item.className}
                                                `}
                                            >
                                                {item.icon}
                                            </a>
                                        ))}

                                        <button
                                            onClick={copyLink}
                                            aria-label="Copy article link"
                                            className="
                                                flex
                                                h-10
                                                w-10
                                                items-center
                                                justify-center
                                                rounded-full
                                                bg-[#002B45]
                                                text-sm
                                                text-white
                                                shadow-sm
                                                transition
                                                hover:-translate-y-1
                                                hover:bg-[#174EA6]
                                            "
                                        >
                                            <FaLink />
                                        </button>

                                    </div>

                                </div>


                                {/* Article body */}
                                <div className="min-w-0">

                                    <div
                                        className="
                                            blog-content
                                            rounded-2xl
                                            border
                                            border-[#E2E8F0]
                                            bg-white
                                            px-5
                                            py-7
                                            shadow-[0_12px_40px_rgba(15,23,42,0.04)]
                                            sm:px-8
                                            sm:py-10
                                            md:px-10
                                            md:py-12
                                        "
                                    >

                                        <div
                                            className="
                                                prose
                                                prose-slate
                                                max-w-none
                                                prose-headings:font-bold
                                                prose-headings:tracking-tight
                                                prose-headings:text-[#0F172A]
                                                prose-h2:mb-5
                                                prose-h2:mt-10
                                                prose-h2:text-2xl
                                                prose-h3:mt-8
                                                prose-h3:text-xl
                                                prose-p:text-[15px]
                                                prose-p:leading-8
                                                prose-p:text-[#475569]
                                                sm:prose-p:text-base
                                                prose-a:font-semibold
                                                prose-a:text-[#174EA6]
                                                prose-a:no-underline
                                                hover:prose-a:underline
                                                prose-strong:text-[#0F172A]
                                                prose-li:text-[#475569]
                                                prose-img:rounded-2xl
                                                prose-img:shadow-lg
                                                prose-blockquote:border-l-[#FFC928]
                                                prose-blockquote:bg-[#F6F9FD]
                                                prose-blockquote:px-5
                                                prose-blockquote:py-2
                                            "
                                            dangerouslySetInnerHTML={{
                                                __html: blog.content,
                                            }}
                                        />

                                    </div>


                                    {/* Mobile share */}
                                    <div
                                        className="
                                            mt-6
                                            rounded-2xl
                                            border
                                            border-[#DCE8F7]
                                            bg-white
                                            p-5
                                            shadow-sm
                                            xl:hidden
                                        "
                                    >

                                        <div className="flex items-center gap-2">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                                <FaShareNodes />
                                            </div>

                                            <div>
                                                <h3 className="text-sm font-bold text-[#0F172A]">
                                                    Share this story
                                                </h3>

                                                <p className="text-[11px] text-[#94A3B8]">
                                                    Help someone discover it
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-4 flex flex-wrap gap-2">

                                            {shareButtons.map((item) => (
                                                <a
                                                    key={item.name}
                                                    href={item.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`
                                                        flex
                                                        h-10
                                                        w-10
                                                        items-center
                                                        justify-center
                                                        rounded-xl
                                                        text-sm
                                                        text-white
                                                        transition
                                                        hover:-translate-y-0.5
                                                        ${item.className}
                                                    `}
                                                >
                                                    {item.icon}
                                                </a>
                                            ))}

                                            <button
                                                onClick={copyLink}
                                                className="
                                                    flex
                                                    items-center
                                                    gap-2
                                                    rounded-xl
                                                    bg-[#002B45]
                                                    px-4
                                                    text-xs
                                                    font-semibold
                                                    text-white
                                                    transition
                                                    hover:bg-[#174EA6]
                                                "
                                            >
                                                <FaLink />

                                                {copied
                                                    ? "Copied!"
                                                    : "Copy Link"}
                                            </button>

                                        </div>

                                    </div>


                                    {/* Article footer */}
                                    <div className="mt-8 border-t border-[#E2E8F0] pt-7">

                                        <Link
                                            to="/blogs"
                                            className="
                                                group
                                                inline-flex
                                                items-center
                                                gap-3
                                                text-sm
                                                font-semibold
                                                text-[#174EA6]
                                            "
                                        >
                                            <span
                                                className="
                                                    flex
                                                    h-9
                                                    w-9
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    bg-[#EAF3FF]
                                                    transition
                                                    group-hover:bg-[#174EA6]
                                                    group-hover:text-white
                                                "
                                            >
                                                ←
                                            </span>

                                            Back to Travel Journal
                                        </Link>

                                    </div>

                                </div>

                            </div>

                        </article>


                        {/* =================================================
                            SIDEBAR
                        ================================================== */}

                        <aside className="space-y-6 lg:pt-2">

                            {/* About */}
                            <div
                                className="
                                    overflow-hidden
                                    rounded-2xl
                                    border
                                    border-[#DCE8F7]
                                    bg-white
                                    shadow-[0_12px_40px_rgba(15,23,42,0.05)]
                                "
                            >

                                <div className="relative bg-[#002B45] px-6 pb-7 pt-7">

                                    <div className="absolute right-0 top-0 h-20 w-20 rounded-full bg-[#174EA6]/30 blur-2xl" />

                                    <div className="relative flex items-center gap-3">

                                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFC928] text-[#002B45]">
                                            <FaCompass />
                                        </div>

                                        <div>
                                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#FFC928]">
                                                Travel Journal
                                            </p>

                                            <h3 className="mt-1 text-lg font-bold text-white">
                                                Explore Dubai
                                            </h3>
                                        </div>

                                    </div>

                                </div>

                                <div className="p-6">

                                    <p className="text-sm leading-7 text-[#64748B]">
                                        Discover travel inspiration,
                                        destination guides, hotel tips and
                                        useful ideas to help you plan a
                                        memorable Dubai getaway.
                                    </p>

                                    <Link
                                        to="/blogs"
                                        className="
                                            mt-5
                                            inline-flex
                                            items-center
                                            gap-2
                                            text-sm
                                            font-bold
                                            text-[#174EA6]
                                            transition
                                            hover:gap-3
                                        "
                                    >
                                        Browse all stories
                                        <FaArrowRight className="text-[10px]" />
                                    </Link>

                                </div>

                            </div>


                            {/* Latest stories */}
                            <div
                                className="
                                    rounded-2xl
                                    border
                                    border-[#DCE8F7]
                                    bg-white
                                    p-5
                                    shadow-[0_12px_40px_rgba(15,23,42,0.05)]
                                    lg:sticky
                                    lg:top-24
                                "
                            >

                                <div className="mb-5 flex items-end justify-between">

                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#174EA6]">
                                            Keep Exploring
                                        </p>

                                        <h3 className="mt-1 text-xl font-bold text-[#0F172A]">
                                            Latest Stories
                                        </h3>
                                    </div>

                                    <Link
                                        to="/blogs"
                                        className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F6FC] text-[#174EA6] transition hover:bg-[#174EA6] hover:text-white"
                                    >
                                        <FaArrowRight className="text-[10px]" />
                                    </Link>

                                </div>


                                <div className="space-y-4">

                                    {latestBlogs
                                        .filter(
                                            (item) =>
                                                item.slug !== blog.slug
                                        )
                                        .slice(0, 5)
                                        .map((item, index) => {

                                            const image = item.thumbnail_image
                                                ? item.thumbnail_image.startsWith(
                                                    "http"
                                                )
                                                    ? item.thumbnail_image
                                                    : `https://uaediscountedhotels.com/uploads/blogs/${item.thumbnail_image}`
                                                : "https://uaediscountedhotels.com/images/default-og.png";

                                            return (
                                                <Link
                                                    key={item.id}
                                                    to={`/blogs/${item.slug}`}
                                                    className="group flex gap-3"
                                                >

                                                    <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl">

                                                        <img
                                                            src={image}
                                                            alt={item.title}
                                                            className="
                                                                h-full
                                                                w-full
                                                                object-cover
                                                                transition
                                                                duration-500
                                                                group-hover:scale-110
                                                            "
                                                        />

                                                        <div className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-md bg-[#002B45]/90 text-[9px] font-bold text-white">
                                                            0{index + 1}
                                                        </div>

                                                    </div>

                                                    <div className="min-w-0 flex-1">

                                                        <p className="
                                                            line-clamp-2
                                                            text-sm
                                                            font-semibold
                                                            leading-5
                                                            text-[#1E293B]
                                                            transition
                                                            group-hover:text-[#174EA6]
                                                        ">
                                                            {item.title}
                                                        </p>

                                                        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#94A3B8]">
                                                            <FaCalendarAlt />
                                                            {formatShortDate(
                                                                item.created_at
                                                            )}
                                                        </div>

                                                    </div>

                                                </Link>
                                            );
                                        })}

                                </div>

                            </div>


                            {/* Booking CTA */}
                            <div
                                className="
                                    relative
                                    overflow-hidden
                                    rounded-2xl
                                    bg-[#002B45]
                                    p-6
                                    shadow-xl
                                "
                            >

                                <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#174EA6]/40 blur-2xl" />

                                <div className="absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-[#FFC928]/15 blur-2xl" />

                                <div className="relative">

                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFC928] text-[#002B45]">
                                        <FaHotel />
                                    </div>

                                    <h3 className="mt-5 text-xl font-bold text-white">
                                        Planning a Dubai Stay?
                                    </h3>

                                    <p className="mt-3 text-sm leading-6 text-white/65">
                                        Find hotels, compare rates and
                                        discover your perfect place to stay.
                                    </p>

                                    <Link
                                        to="/hotels"
                                        className="
                                            mt-6
                                            flex
                                            items-center
                                            justify-center
                                            gap-2
                                            rounded-xl
                                            bg-white
                                            px-5
                                            py-3
                                            text-sm
                                            font-bold
                                            text-[#002B45]
                                            transition
                                            hover:bg-[#FFC928]
                                        "
                                    >
                                        Explore Hotels
                                        <FaArrowRight className="text-[10px]" />
                                    </Link>

                                </div>

                            </div>

                        </aside>

                    </div>

                </section>


                {/* =====================================================
                    RELATED STORIES
                ====================================================== */}

                {relatedBlogs.length > 0 && (
                    <section className="border-t border-[#DCE8F7] bg-white">

                        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">

                            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">

                                <div>
                                    <div className="flex items-center gap-3">

                                        <span className="h-px w-8 bg-[#FFC928]" />

                                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#174EA6]">
                                            You May Also Like
                                        </span>

                                    </div>

                                    <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">
                                        More Travel Inspiration
                                    </h2>
                                </div>

                                <Link
                                    to="/blogs"
                                    className="
                                        inline-flex
                                        items-center
                                        gap-2
                                        text-sm
                                        font-bold
                                        text-[#174EA6]
                                    "
                                >
                                    View all stories
                                    <FaArrowRight className="text-xs" />
                                </Link>

                            </div>


                            <div className="mt-9 grid grid-cols-1 gap-6 md:grid-cols-3">

                                {relatedBlogs.map((item, index) => {

                                    const image = item.thumbnail_image
                                        ? item.thumbnail_image.startsWith(
                                            "http"
                                        )
                                            ? item.thumbnail_image
                                            : `https://uaediscountedhotels.com/uploads/blogs/${item.thumbnail_image}`
                                        : "https://uaediscountedhotels.com/images/default-og.png";

                                    return (
                                        <Link
                                            key={item.id}
                                            to={`/blogs/${item.slug}`}
                                            className="
                                                group
                                                overflow-hidden
                                                rounded-2xl
                                                border
                                                border-[#DCE8F7]
                                                bg-white
                                                shadow-sm
                                                transition
                                                duration-300
                                                hover:-translate-y-1
                                                hover:shadow-xl
                                            "
                                        >

                                            <div className="relative h-52 overflow-hidden">

                                                <img
                                                    src={image}
                                                    alt={item.title}
                                                    className="
                                                        h-full
                                                        w-full
                                                        object-cover
                                                        transition
                                                        duration-700
                                                        group-hover:scale-105
                                                    "
                                                />

                                                <div className="absolute inset-0 bg-gradient-to-t from-[#002B45]/80 via-transparent to-transparent" />

                                                <div className="absolute left-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg bg-white/90 text-xs font-bold text-[#174EA6] backdrop-blur">
                                                    0{index + 1}
                                                </div>

                                                {item.category?.name && (
                                                    <span
                                                        className="
                                                            absolute
                                                            bottom-4
                                                            left-4
                                                            rounded-full
                                                            bg-[#FFC928]
                                                            px-3
                                                            py-1
                                                            text-[9px]
                                                            font-bold
                                                            uppercase
                                                            tracking-wider
                                                            text-[#002B45]
                                                        "
                                                    >
                                                        {item.category.name}
                                                    </span>
                                                )}

                                            </div>

                                            <div className="p-5">

                                                <div className="flex items-center gap-2 text-[10px] text-[#94A3B8]">
                                                    <FaCalendarAlt />
                                                    {formatShortDate(
                                                        item.created_at
                                                    )}
                                                </div>

                                                <h3
                                                    className="
                                                        mt-3
                                                        line-clamp-2
                                                        text-lg
                                                        font-bold
                                                        leading-6
                                                        text-[#0F172A]
                                                        transition
                                                        group-hover:text-[#174EA6]
                                                    "
                                                >
                                                    {item.title}
                                                </h3>

                                                <div className="mt-5 flex items-center justify-between">

                                                    <span className="text-xs font-semibold text-[#174EA6]">
                                                        Read Story
                                                    </span>

                                                    <span
                                                        className="
                                                            flex
                                                            h-8
                                                            w-8
                                                            items-center
                                                            justify-center
                                                            rounded-full
                                                            bg-[#F1F6FC]
                                                            text-[#174EA6]
                                                            transition
                                                            group-hover:bg-[#174EA6]
                                                            group-hover:text-white
                                                        "
                                                    >
                                                        <FaArrowRight className="text-[10px]" />
                                                    </span>

                                                </div>

                                            </div>

                                        </Link>
                                    );
                                })}

                            </div>

                        </div>

                    </section>
                )}


                {/* =====================================================
                    FINAL CTA
                ====================================================== */}

                <section className="bg-[#F6F9FD] px-4 py-12 sm:px-6 md:py-16">

                    <div
                        className="
                            relative
                            mx-auto
                            max-w-7xl
                            overflow-hidden
                            rounded-[28px]
                            bg-[#002B45]
                            px-6
                            py-12
                            shadow-2xl
                            sm:px-10
                            md:px-16
                            md:py-16
                        "
                    >

                        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#174EA6]/30 blur-3xl" />

                        <div className="absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-[#FFC928]/10 blur-3xl" />

                        <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

                        <div className="relative z-10 mx-auto max-w-3xl text-center">

                            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#FFC928]">
                                <FaHotel />
                                Your Dubai Journey Starts Here
                            </span>

                            <h2 className="mt-5 text-3xl font-bold tracking-tight !text-white sm:text-4xl md:text-5xl">
                                Ready to Discover Dubai?
                            </h2>

                            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-white/65 sm:text-base">
                                Turn travel inspiration into your next
                                unforgettable stay. Explore Dubai hotels and
                                find the right place for your journey.
                            </p>

                            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">

                                <Link
                                    to="/hotels"
                                    className="
                                        inline-flex
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-xl
                                        bg-[#FFC928]
                                        px-6
                                        py-3.5
                                        text-sm
                                        font-bold
                                        text-[#002B45]
                                        shadow-lg
                                        transition
                                        hover:-translate-y-0.5
                                        hover:bg-[#FFD84D]
                                    "
                                >
                                    Explore Hotels
                                    <FaArrowRight className="text-xs" />
                                </Link>

                                <Link
                                    to="/destinations"
                                    className="
                                        inline-flex
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-xl
                                        border
                                        border-white/20
                                        bg-white/5
                                        px-6
                                        py-3.5
                                        text-sm
                                        font-semibold
                                        text-white
                                        backdrop-blur
                                        transition
                                        hover:bg-white/10
                                    "
                                >
                                    Explore Destinations
                                </Link>

                            </div>

                        </div>

                    </div>

                </section>

            </main>
        </>
    );
}